/**
 * `<HealthCard>` — the per-panel chrome inside Corpus Health (Phase 4d).
 *
 * Drop-in replacement for `<Panel>` on the Health surface — same prop shape
 * (title + anchorId + actions + children) but renders against the v2
 * `.card` / `.card-head` / `.card-body` primitives. Keeps Health visually
 * consistent with Sources / Settings without rewriting panel internals.
 */

import type { ReactNode } from 'react';

interface HealthCardProps {
  title: ReactNode;
  /** Inline subtitle alongside the title (e.g. "3 flagged · merge optional"). */
  sub?: ReactNode;
  /** Right-aligned action cluster — buttons, segmented controls. */
  actions?: ReactNode;
  /** Anchor id for `?panel=…` deep-link scroll-into-view. */
  anchorId?: string;
  children: ReactNode;
}

export function HealthCard({
  title,
  sub,
  actions,
  anchorId,
  children,
}: HealthCardProps): JSX.Element {
  return (
    <div className="card" id={anchorId} style={{ marginBottom: 'var(--s-4)' }}>
      <header className="card-head">
        <h3 className="card-title">{title}</h3>
        {sub && <span className="card-sub">{sub}</span>}
        {actions && (
          <>
            <span style={{ flex: 1 }} />
            {actions}
          </>
        )}
      </header>
      <div className="card-body">{children}</div>
    </div>
  );
}
